const oswald = "'Oswald', sans-serif";
// Cream paper behind the collage cut-out (the wall's off-white).
const PAPER = '#FEF3EF';

/**
 * Portrait — the woman's face, in a round frame.
 *
 * With a `url` it renders her collage portrait in its own colours on a cream
 * paper disc, zoomed slightly towards her face (the collages carry a
 * transparent margin). Pass `muted` for a greyscale print, e.g. while the
 * unit is still in progress.
 * Without a `url` it falls back to the stencil: the woman's initial letter in
 * the same circle, inked in `color`.
 */
export function Portrait({
  url,
  initial,
  name,
  color,
  size = 40,
  muted = false,
  style,
}: {
  url: string | null | undefined;
  initial: string;
  name?: string | null;
  color: string;
  size?: number;
  muted?: boolean;
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
    ...style,
  };

  if (url) {
    return (
      <div
        style={{
          ...base,
          border: `${size >= 32 ? 2.5 : 1}px solid #FFFFFF`,
          background: PAPER,
          boxShadow: '0 2px 6px rgba(26,16,24,0.25)',
        }}
      >
        <img
          src={url}
          alt={name ?? ''}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
            display: 'block',
            transform: 'scale(1.18)',
            transformOrigin: '50% 38%',
            filter: muted ? 'grayscale(1) contrast(1.05)' : undefined,
          }}
        />
      </div>
    );
  }

  return (
    <div
      aria-label={name ?? undefined}
      style={{
        ...base,
        background: `${color}12`,
        border: `${size >= 32 ? 2.5 : 1}px solid ${color}35`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: oswald,
        fontSize: size * 0.52,
        fontWeight: 700,
        color,
        letterSpacing: '0.05em',
      }}
    >
      {initial}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${color}06 2px, ${color}06 4px)`,
        }}
      />
    </div>
  );
}

/** First letter of a name, for the stencil fallback. */
export function initialOf(
  name: string | null | undefined,
  fallback = '?',
): string {
  const c = (name ?? '').trim().charAt(0);
  return c ? c.toUpperCase() : fallback;
}
